import type { AdminUnit, ExternalUnit, MappingInput, UnitMapping } from './types'

/**
 * The mappings endpoint upserts: it only touches the unit numbers in the body, and a row with both
 * ids null deletes that one mapping. So a single row change is a single-row request.
 *
 * `changes` is keyed by unit id; a null value unlinks that unit.
 */
export function buildMappingPayload(units: AdminUnit[], changes: Map<string, UnitMapping | null>): MappingInput[] {
  const byId = new Map(units.map((unit) => [unit.id, unit]))
  const payload: MappingInput[] = []
  for (const [unitId, mapping] of changes) {
    const unit = byId.get(unitId)
    if (!unit) continue
    payload.push({
      unitNumber: unit.number,
      externalUnitId: mapping?.externalUnitId ?? null,
      externalTypeId: mapping?.externalTypeId ?? null,
    })
  }
  return payload
}

/** External units grouped by type, for the optgroups in the mapping select. */
export function groupByType(units: ExternalUnit[]): { label: string; units: ExternalUnit[] }[] {
  const groups = new Map<string, ExternalUnit[]>()
  for (const unit of units) {
    const label = unit.typeLabel ?? unit.externalTypeId ?? 'Zonder type'
    const bucket = groups.get(label)
    if (bucket) bucket.push(unit)
    else groups.set(label, [unit])
  }
  return [...groups.entries()]
    .map(([label, list]) => ({ label, units: list }))
    .sort((a, b) => a.label.localeCompare(b.label, 'nl'))
}

/** The distinct external types, for the bulk action and for type-only connectors. */
export function externalTypes(units: ExternalUnit[]): { id: string; label: string }[] {
  const types = new Map<string, string>()
  for (const unit of units) {
    if (!unit.externalTypeId) continue
    if (!types.has(unit.externalTypeId)) types.set(unit.externalTypeId, unit.typeLabel ?? unit.externalTypeId)
  }
  return [...types.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'nl'))
}
